import { useEffect, useState } from "react";
import {
  Search,
  PlusSquare,
  Edit,
  Trash2,
  StickyNoteIcon,
  Link as LinkIcon,
  XCircle,
  CheckCircle,
  Wallpaper,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useBackgroundPattern } from "../../hooks/useBackgroundPattern";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import BackgroundPatternForm from "./BackgroundPatternForm";
import { toast } from "react-toastify";
import { useResolvedPath } from "react-router-dom";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import { useFileUpload } from "../../hooks/useFileUplode";
import ConfirmationModal from "../../components/ui/ConfirmationModal";

function BackgroundPattern() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBackgroundPattern, setSelectedBackgroundPattern] =
    useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view, edit, create
  const [backgroundPatternsList, setBackgroundPatternsList] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [exclusiveDescription, setExclusiveDescription] = useState("");
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: Math.ceil(backgroundPatternsList.length / 10),
    showing: 10,
    total: 0,
  });

  const queryClient = useQueryClient();

  const {
    fetchBackgroundPattern,
    createBackgroundPattern,
    updateBackgroundPattern,
    deleteBackgroundPattern,
    toggleBackgroundPatternStatus,
    toggleDefaultBackgroundPattern,
    toggleIsExclusive,
  } = useBackgroundPattern({
    pagination: { page: pageData.currentPage, limit: 10 },
  });
  const { fileUpload, fileDelete } = useFileUpload();

  const backgroundPatternsQuery = useQuery({
    queryKey: ["backgroundPattern", pageData.currentPage],
    queryFn: () =>
      fetchBackgroundPattern({
        pagination: {
          backgroundPattern: pageData.currentPage,
          limit: pageData.showing,
        },
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!backgroundPatternsQuery.isLoading && backgroundPatternsQuery.data) {
      const response = backgroundPatternsQuery.data;
      setBackgroundPatternsList(response.data.patterns || []);
      setPageData({
        currentPage: response.data.pagination?.currentPage || 1,
        totalPages: response.data.pagination?.lastPage || 1,
        total: response.data.pagination?.total || 0,
        showing: response.data.pagination?.showing || 10,
      });
    }
  }, [backgroundPatternsQuery.data, backgroundPatternsQuery.isLoading]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const openModal = (backgroundPattern, mode) => {
    setSelectedBackgroundPattern(backgroundPattern);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBackgroundPattern(null);
  };

  const handleBackgroundPatternChange = (backgroundPattern) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: backgroundPattern,
    }));
  };

  const filteredBackgroundPatterns = backgroundPatternsList.filter(
    (backgroundPattern) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          (backgroundPattern.title &&
            backgroundPattern.title.toLowerCase().includes(query)) ||
          (backgroundPattern.backgroundPattern &&
            backgroundPattern.backgroundPattern.toLowerCase().includes(query))
        );
      }
      return true;
    }
  );

  const handleUpdate = (backgroundPattern) => {
    openModal(backgroundPattern, "edit");
  };

  const handleDelete = {
    onDelete: (backgroundPattern) =>
      mutation.deleteBackgroundPattern.mutate(backgroundPattern._id, {
        onSuccess: (data) => {
          toast.success(data.message || "Background Pattern Deleted");
          setDeleteModal(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to delete backgroundPattern");
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (backgroundPattern) => {
      setDeleteModal(true);
      setSelectedBackgroundPattern(backgroundPattern);
    },
  };

  const toggleStatus = (backgroundPattern) => {
    mutation.toggleStatus.mutate(backgroundPattern._id, {
      onSuccess: (data) => {
        toast.success("Status updated successfully");
      },
      onError: (error) => toast.error("Failed to update status"),
    });
  };

  const toggleDefault = (backgroundPattern) => {
    mutation.toggleDefault.mutate(backgroundPattern._id, {
      onSuccess: (data) => {
        toast.success("Default status updated successfully");
      },
      onError: (error) => toast.error("Failed to update default status"),
    });
  };

  const toggleExclusive = (backgroundPattern, allowExclusive) => {
    mutation.toggleExclusive.mutate(
      { id: backgroundPattern._id, allowExclusive },
      {
        onSuccess: (data) => {
          if (data.status === 405 && data?.data?.open_confirmation_modal) {
            setExclusiveDescription(data.message);
            setConfirmationModal(true);
          } else {
            setConfirmationModal(false);
            toast.success(data.message);
          }
        },
        onError: (error) => {
          toast.error(error.message);
        },
      }
    );
  };

  const mutation = {
    createBackgroundPattern: useMutation({
      mutationFn: (body) => {
        if (modalMode === "edit") {
          return updateBackgroundPattern(body);
        }
        return createBackgroundPattern(body);
      },
    }),
    deleteBackgroundPattern: useMutation({
      mutationFn: (id) => deleteBackgroundPattern(id),
    }),
    toggleStatus: useMutation({
      mutationFn: async (id) => await toggleBackgroundPatternStatus(id),
    }),
    toggleDefault: useMutation({
      mutationFn: async (id) => await toggleDefaultBackgroundPattern(id),
    }),
    toggleExclusive: useMutation({
      mutationFn: async (data) =>
        await toggleIsExclusive(data.id, data.allowExclusive),
    }),
  };

  const handleSubmit = (body) => {
    if (modalMode === "edit") {
      body._id = selectedBackgroundPattern._id;
    }

    mutation.createBackgroundPattern.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["backgroundPattern"]);
        toast.success(
          data?.data?.message ||
            `Background Pattern ${
              modalMode === "edit" ? "Updated" : "Created"
            } successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save backgroundPattern");
      },
    });
  };

  return (
    <div>
      <main className="">
        {/* Header and Add Button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Background Pattern</h1>
          <button
            onClick={() => openModal({}, "create")}
            className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            <PlusSquare className="mr-2 h-4 w-4" />
            Add New Background Pattern
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search backgroundPatterns..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Table or Skeleton */}
          {backgroundPatternsQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Title
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Image
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Status
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Default
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBackgroundPatterns.length > 0 ? (
                    filteredBackgroundPatterns.map((backgroundPattern) => (
                      <tr
                        key={backgroundPattern._id}
                        className="hover:bg-gray-50 border-b"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <Wallpaper className="h-4 w-4 text-purple-800" />
                            </div>
                            <div className="ml-3">
                              <div className={`font-medium text-gray-500`}>
                                {backgroundPattern.title}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {backgroundPattern.image ? (
                            <img
                              src={backgroundPattern.image}
                              alt={backgroundPattern.title || "Image"}
                              className="h-10 w-10 object-cover rounded"
                            />
                          ) : (
                            <div className="flex items-center">
                              <LinkIcon className="mr-1 h-4 w-4 text-gray-400" />
                              <span>No Image</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {mutation.toggleStatus.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                                backgroundPattern.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                              onClick={() => {
                                toggleStatus(backgroundPattern);
                              }}
                              title="Toggle Status"
                            >
                              {backgroundPattern.is_active ? (
                                <CheckCircle className="mr-1 h-3 w-3" />
                              ) : (
                                <XCircle className="mr-1 h-3 w-3" />
                              )}
                              {backgroundPattern.is_active
                                ? "Active"
                                : "Inactive"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {mutation.toggleDefault.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                                backgroundPattern.is_default
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                              onClick={() => {
                                toggleDefault(backgroundPattern);
                              }}
                              title="Toggle Default"
                            >
                              {backgroundPattern.is_default ? (
                                <CheckCircle className="mr-1 h-3 w-3" />
                              ) : (
                                <XCircle className="mr-1 h-3 w-3" />
                              )}
                              {backgroundPattern.is_default
                                ? "Default"
                                : "Not Default"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(backgroundPattern.created_at).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() =>
                                openModal(backgroundPattern, "view")
                              }
                              className="text-gray-400 hover:text-gray-500"
                              title="View Details"
                            >
                              <StickyNoteIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUpdate(backgroundPattern)}
                              className="text-blue-400 hover:text-blue-500"
                              title="Edit Background Pattern"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              className="text-red-400 hover:text-red-500"
                              onClick={() =>
                                handleDelete.onOpen(backgroundPattern)
                              }
                              title="Delete Background Pattern"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No backgroundPatterns found matching your search
                        criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handleBackgroundPatternChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedBackgroundPattern)}
        title="Background Pattern"
      />
      <ConfirmationModal
        isOpen={confirmationModal}
        onClose={() => setConfirmationModal(false)}
        onSuccess={() => toggleExclusive(selectedBackgroundPattern, true)}
        description={exclusiveDescription}
        title="Make backgroundPattern exclusive"
      />
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Background Pattern Details"
                  : modalMode === "edit"
                  ? "Edit Background Pattern"
                  : "Create Background Pattern"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <BackgroundPatternForm
                mode={modalMode}
                defaultValues={
                  selectedBackgroundPattern
                    ? {
                        ...selectedBackgroundPattern,
                        created_at: moment(
                          selectedBackgroundPattern.created_at
                        ).format("YYYY-MM-DD"),
                        contentIds: selectedBackgroundPattern.contentIds || [],
                      }
                    : {}
                }
                onCancel={closeModal}
                backgroundPatternsList={backgroundPatternsList}
                onSubmit={(data) => handleSubmit(data)}
                submissionLoading={mutation.createBackgroundPattern.isLoading}
                fileUpload={fileUpload}
                fileDelete={fileDelete}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BackgroundPattern;
